// Lumi public library — the original catalog, served from the existing public
// S3 buckets (both public-read, CORS `*`):
//   • source text:  https://lumireader.s3.amazonaws.com/public_library/<title>.txt
//   • cover art:     https://lumi-public-book-cover-art.s3.amazonaws.com/<cover>
// Reading levels mirror the original `difficultyMap` scale. (Levels are curated
// here — the original stored them in the now-retired Postgres `publicLibrary`
// table; the S3 files carry no level metadata.)

const COVER_BASE = "https://lumi-public-book-cover-art.s3.amazonaws.com";
const TEXT_BASE = "https://lumireader.s3.amazonaws.com/public_library";

export interface PublicBook {
  id: string;
  /** Display title; also the exact `<title>.txt` key under public_library/. */
  title: string;
  /** Exact cover object key in the cover-art bucket. */
  cover: string;
  /** Reading level on the original 1–12 scale. */
  level: string;
}

export const DIFFICULTY: Record<string, string> = {
  "1": "Newbie",
  "3": "Beginner",
  "5": "Intermediate",
  "7": "Upper Intermediate",
  "9": "Advanced",
  "12": "Fluent",
};

export function difficultyLabel(level: string | null | undefined): string {
  if (!level) return "All levels";
  return DIFFICULTY[level] ?? "All levels";
}

export function coverUrl(book: PublicBook): string {
  return `${COVER_BASE}/${encodeURIComponent(book.cover)}`;
}

export function textUrl(book: PublicBook): string {
  return `${TEXT_BASE}/${encodeURIComponent(book.title)}.txt`;
}

// Pre-translated bundle (when one exists for this title+language): nested pages
// of aligned `{ english, <targetname> }` pairs at
// `public_library/translated/<Title>_<code>.json`. Used to skip re-translation.
export function translatedUrl(book: PublicBook, langCode: string): string {
  return `${TEXT_BASE}/translated/${encodeURIComponent(book.title)}_${langCode}.json`;
}

// The featured pick — Aesop, matching the home-screen "Get Started with Aesop's
// Fables" CTA. The original featured `publicLibrary` id=1.
export const FEATURED_ID = "aesop";

// Ordered roughly by familiarity (the original sorted by usage count, which
// lived in the DB). Cover keys reflect the real, sometimes-irregular S3 names.
export const PUBLIC_CATALOG: PublicBook[] = [
  { id: "aesop", title: "The Fables of Aesop", cover: "aesop.jpeg", level: "1" },
  { id: "alice", title: "Alice in Wonderland", cover: "Alice in Wonderland.jpg", level: "3" },
  { id: "sherlock", title: "The Adventures of Sherlock Holmes", cover: "sherlock.jpeg", level: "7" },
  { id: "gatsby", title: "The Great Gatsby", cover: "gatsby.jpeg", level: "7" },
  { id: "frankenstein", title: "Frankenstein; Or, The Modern Prometheus", cover: "frankenstein.jpeg", level: "7" },
  { id: "peter-pan", title: "Peter Pan", cover: "Peter Pan.jpg", level: "3" },
  { id: "tom-sawyer", title: "Tom Sawyer", cover: "Tom Sawyer.jpg", level: "5" },
  { id: "huck-finn", title: "Adventures of Huckleberry Finn", cover: "Adventures of Huckleberry Finn.jpg", level: "5" },
  { id: "jekyll-hyde", title: "Dr. Jekyll and Mr. Hyde", cover: "Dr. Jekyll and Mr. Hyde.jpg", level: "7" },
  { id: "two-cities", title: "A Tale of Two Cities", cover: "two_cities.jpeg", level: "9" },
  { id: "arabian-nights", title: "Arabian Nights", cover: "Arabian Nights.jpg", level: "7" },
  { id: "winnie-the-pooh", title: "Winnie the Pooh", cover: "Winnie the Pooh.jpg", level: "1" },
  { id: "souls-black-folk", title: "The Souls of Black Folk", cover: "The Souls of Black Folk.jpg", level: "9" },
  { id: "grimms", title: "Grimms Fairy Tales", cover: "Grimms Fairy Tales.jpg", level: "3" },
  { id: "andersens", title: "Andersen's Fairy Tales", cover: "Andersen's Fairy Tales.jpg", level: "3" },
  { id: "children-of-odin", title: "The Children of Odin", cover: "The Children of Odin.jpg", level: "5" },
  { id: "viking-tales", title: "Viking Tales", cover: "Viking Tales.jpg", level: "5" },
  { id: "princess-goblin", title: "The Princess and the Goblin", cover: "The Princess and the Goblin.jpg", level: "5" },
  { id: "american-fairy", title: "American Fairy Tales", cover: "American Fairy Tales.jpg", level: "3" },
  { id: "chinese-fairy", title: "Chinese Fairy Tales", cover: "Chinese-Fairy-Tales.jpeg", level: "3" },
  { id: "japanese-folk", title: "Japanese Folk Stories", cover: "Japanese Folk Stories.jpg", level: "3" },
  { id: "korean-folk", title: "Korean Folk Tales", cover: "Korean Folk Tales.jpg", level: "3" },
  { id: "indian-fairy", title: "Indian Fairy Tales", cover: "Indian Fairy Tales.jpg", level: "3" },
  { id: "russian-fairy", title: "Russian Fairy Tales", cover: "Russian Fairy Tales.jpg", level: "3" },
  { id: "french-fairy", title: "French Fairy Tales", cover: "French Fairy Tales.jpg", level: "3" },
  { id: "dutch-fairy", title: "Dutch Fairy Tales", cover: "Dutch Fairy Tales.jpg", level: "3" },
  { id: "greek-fairy", title: "Greek Fairy Tales", cover: "Greek Fairy Tales.jpg", level: "3" },
  { id: "swedish-fairy", title: "Swedish Fairy Tales", cover: "Swedish Fairy Tales.jpg", level: "3" },
  { id: "turkish-fairy", title: "Turkish Fairy Tales", cover: "Turkish Fairy Tales.jpg", level: "3" },
  { id: "himalayan-folk", title: "Himalayan Folk Tales", cover: "Himalayan Folk Tales.jpg", level: "5" },
  { id: "west-african-folk", title: "West African Folk Tales", cover: "West African Folk Tales.jpg", level: "5" },
  { id: "south-african-folk", title: "South African Folk Tales", cover: "South African Folk Tales.jpg", level: "5" },
];
